import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { notificationCenterAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  LayoutDashboard,
  FileText,
  CalendarDays,
  ClipboardList,
  Receipt,
  BarChart3,
  Upload,
  Building2,
  UserCog,
  Bell,
  BellRing,
  History,
  Timer,
  Shield,
  ShieldCheck,
  LogOut,
  ScanLine,
  Palette,
  Sun,
  Moon,
  Crown,
  Menu,
  X,
  BotMessageSquare,
  FileCheck,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { path: '/cases', label: 'Cases', icon: FileText, testId: 'nav-cases' },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays, testId: 'nav-calendar' },
  { path: '/forms', label: 'Form Fill', icon: ClipboardList, testId: 'nav-forms' },
  { path: '/invoices', label: 'Invoices', icon: Receipt, testId: 'nav-invoices' },
  { path: '/reports', label: 'Reports', icon: BarChart3, testId: 'nav-reports' },
  { path: '/cases/import', label: 'Bulk Import', icon: Upload, testId: 'nav-bulk-import' },
  { path: '/cases/batch-scan', label: 'Batch Scan', icon: ScanLine, testId: 'nav-batch-scan', feature: 'batch_scanner' },
  { path: '/ai-assistant', label: 'AI Assistant', icon: BotMessageSquare, testId: 'nav-ai-assistant' },
  { path: '/efiling', label: 'eFiling', icon: FileCheck, testId: 'nav-efiling', feature: 'online_filing' },
];

const adminItems = [
  { path: '/admin/organizations', label: 'Organizations', icon: Building2, testId: 'nav-organizations', superAdminOnly: true },
  { path: '/admin/users', label: 'User Management', icon: UserCog, testId: 'nav-admin-users', superAdminOnly: true },
  { path: '/admin/billing', label: 'Billing', icon: CreditCard, testId: 'nav-billing', superAdminOnly: true },
  { path: '/admin/email-templates', label: 'Email Templates', icon: Palette, testId: 'nav-email-templates', superAdminOnly: true },
  { path: '/admin/notifications', label: 'Email Logs', icon: Bell, testId: 'nav-notifications', adminOnly: true },
];

const bottomItems = [
  { path: '/notifications', label: 'Alerts', icon: BellRing, testId: 'nav-notification-center', showBadge: true },
  { path: '/settings/security', label: 'Security', icon: ShieldCheck, testId: 'nav-security' },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: History, testId: 'nav-audit-logs', superAdminOnly: true },
  { path: '/admin/scheduler', label: 'Scheduled Jobs', icon: Timer, testId: 'nav-scheduler', superAdminOnly: true },
];

export default function Sidebar() {
  const { user, logout, isSuperAdmin, isAdmin, organizationName, hasFeature } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const fetchUnreadCount = async () => {
    try {
      const res = await notificationCenterAPI.getUnreadCount();
      setUnreadNotifications(res.data.unread_count || 0);
    } catch (error) {
      // Silently fail
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const locked = item.feature && !hasFeature(item.feature);
    
    if (locked) {
      return (
        <div
          key={item.path}
          className="flex items-center gap-3 px-4 py-3 rounded-lg opacity-40 cursor-not-allowed select-none"
          data-testid={item.testId}
          title="Premium Feature — Contact admin to enable"
        >
          <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
          <span className="text-muted-foreground">{item.label}</span>
          <Badge className="ml-auto bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] px-1.5 py-0 border border-amber-500/20 hover:bg-amber-500/15">
            <Crown className="w-3 h-3 mr-0.5" />
            Premium
          </Badge>
        </div>
      );
    }
    
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-body ${
          active 
            ? 'bg-primary/10 text-primary font-medium' 
            : 'text-muted-foreground hover:bg-secondary'
        }`}
        data-testid={item.testId}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        {item.label}
        {item.showBadge && unreadNotifications > 0 && (
          <Badge className="ml-auto bg-error text-white text-xs">{unreadNotifications}</Badge>
        )}
      </Link>
    );
  };

  const shouldShowItem = (item) => {
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between mb-8">
        <img src="/logo.png" alt="NewSlate" className="h-10 object-contain" />
        <button
          onClick={() => setMobileOpen(false)}
          className="md:hidden p-1 rounded-lg text-muted-foreground hover:bg-secondary"
          data-testid="sidebar-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Organization Badge */}
      {!isSuperAdmin && organizationName && (
        <div className="mb-4 px-4 py-2 bg-secondary rounded-lg">
          <p className="font-body text-xs text-muted-foreground uppercase tracking-wider">Organization</p>
          <p className="font-heading text-sm font-semibold text-foreground truncate">{organizationName}</p>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 space-y-2 overflow-y-auto">
        {navItems.map(item => renderNavItem(item))}
        
        {/* Admin Section */}
        {(isSuperAdmin || isAdmin) && adminItems.some(shouldShowItem) && (
          <>
            <div className="pt-4 pb-2">
              <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Administration
              </p>
            </div>
            {adminItems.filter(shouldShowItem).map(item => renderNavItem(item))}
          </>
        )}

        {/* Bottom Items (Alerts, Audit, Scheduler) */}
        <div className="pt-2">
          {bottomItems.filter(shouldShowItem).map(item => renderNavItem(item))}
        </div>
      </nav>

      {/* User Section */}
      <div className="border-t border-border pt-4">
        <div className="px-4 py-2 mb-2">
          <div className="flex items-center gap-2">
            <p className="font-body text-sm font-medium text-foreground truncate">{user?.name}</p>
            {isSuperAdmin && <Shield className="w-4 h-4 text-primary" />}
          </div>
          <p className="font-body text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            className="flex-1 justify-start text-muted-foreground font-body"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Sign Out
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="text-muted-foreground hover:text-foreground shrink-0"
            data-testid="theme-toggle-btn"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-5 h-5" strokeWidth={1.5} /> : <Moon className="w-5 h-5" strokeWidth={1.5} />}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-50 md:hidden p-2 rounded-lg bg-card border border-border shadow-lg text-foreground"
        data-testid="sidebar-toggle-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Desktop sidebar - always visible */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border p-6 flex-col z-40">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar - slide in/out */}
      <aside
        className={`md:hidden fixed left-0 top-0 bottom-0 w-72 bg-card border-r border-border p-6 flex flex-col z-50 transform transition-transform duration-200 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
